import React, { useState, useRef, useEffect, CSSProperties, useMemo } from "react";
import "./ImageViewer.css";
import ViewerHeader from "./components/ViewerHeader/ViewerHeader";
import ViewerMain from "./components/ViewerMain/ViewerMain";
import ViewerFooter from "./components/ViewerFooter/ViewerFooter";
import Requester from "../../class/Requester";
import tifFileMap from "../../utils/tifFileMap";
import pdfFileMap from "../../utils/pdfFileMap";
import speedFirstTpl from "../../assets/templates/ReadBarcodes_SpeedFirst.json";
import { useSelector, useDispatch } from 'react-redux';
import { Response, updateResponse } from "../../store/features/responseSlice";
import { addExLog, deleteExLog } from "../../store/features/exLogSlice";
import { DivRef, InputRef, AnyRef, PreArrRef, UniteTypeTpl } from "../../utils/typesAndMaps";
import { updateState } from "../../store/features/generalStatesSlice";
import { device } from "../../utils/helpers";
import { updateImageDataState } from "../../store/features/imagesDataSlice";
import { RootStore } from "../../store";
import { warpTemplateFromGlobalSettings } from "../../utils/templateUIConfigConverter";
import { updateStyles } from "../../store/features/nodeStylesSlice";

interface ImageViewerProps {
  logPreRef: PreArrRef
}

export default function ImageViewer({ logPreRef }: ImageViewerProps) {
  const contentRef: DivRef = useRef(null);
  const panelRef: InputRef = useRef(null);
  const oldResponse: AnyRef = useRef(null);
  const timer: any = useRef(null);

  const [errorMessage, setErrorMessage] = useState("");

  const imagesData = useSelector((store: RootStore) => store.imagesData);
  const globalSettings = useSelector((store: RootStore) => store.globalSettings);
  const generalStates = useSelector((store: RootStore) => store.generalStates);
  const nodeStyles = useSelector((store: RootStore) => store.nodeStyles);

  const imageDataRef = useRef(imagesData);
  const nodeStylesRef = useRef(nodeStyles);
  const dispatch = useDispatch();

  const apiCallbacks = useMemo(() => {
    return {
      uploadImg: (isExpired?: boolean) => {
        dispatch(updateState({ stateName: "loadingContent", value: isExpired ? "Re-uploading file" : "uploading file" }));
        if (imageDataRef.current[generalStates.curImgIdx].isPdf) {
          Requester.uploadPdfToServer(imageDataRef.current[generalStates.curImgIdx].name, pdfFileMap.get(imageDataRef.current[generalStates.curImgIdx].name)!, apiCallbacks);
        } else if (imageDataRef.current[generalStates.curImgIdx].isTif) {
          Requester.uploadPdfToServer(imageDataRef.current[generalStates.curImgIdx].name, tifFileMap.get(imageDataRef.current[generalStates.curImgIdx].name)!, apiCallbacks);
        } else if (imageDataRef.current[generalStates.curImgIdx].name.search(/\.webp$/i) !== -1) {
          // If it is a .webp file, convert it to .png before sending it to server.
          // Convert imageDataRef.current[generalStates.curImgIdx].url to to HTMLImageElement.
          const image = new Image();
          image.src = imageDataRef.current[generalStates.curImgIdx].url!;
          image.onload = () => {
            // Draw 'image' to canvas, then save it as a .png file.
            const canvas = document.createElement("canvas");
            canvas.width = image.width;
            canvas.height = image.height;
            canvas.getContext("2d")!.drawImage(image, 0, 0);
            canvas.toBlob((blob) => {
              Requester.uploadPdfToServer(imageDataRef.current[generalStates.curImgIdx].name.replace(/\.webp$/i, ".png"), blob!, apiCallbacks);
            }, "image/png");
          };
          image.onerror = () => {
            throw new Error("Load image failed.");
          };
        } else {
          Requester.uploadImgToServer(imageDataRef.current[generalStates.curImgIdx], apiCallbacks);
        }
      },
      decode: (settings: UniteTypeTpl) => {
        oldResponse.current = null;
        dispatch(updateStyles({ errorMsgStyles: { display: "none" } }));
        dispatch(updateState({ stateName: "loadingContent", value: "sending decode request" }));
        if (imageDataRef.current[generalStates.curImgIdx].sampleImg || !generalStates.decodingMode) {
          Requester.sendDecodeRequest(imageDataRef.current[generalStates.curImgIdx], [settings], globalSettings, apiCallbacks);
        } else {
          Requester.sendDecodeRequest(imageDataRef.current[generalStates.curImgIdx], [speedFirstTpl, settings], globalSettings, apiCallbacks);
        }
      },
      uploadImgCallBack: () => {
        dispatch(updateImageDataState({ currentImageIndex: generalStates.curImgIdx, uploadState: true }));
        const template = warpTemplateFromGlobalSettings(globalSettings);
        apiCallbacks.decode(template);
      },
      uploadImgErrorCallBack: (statusText: string) => {
        const errorText = "ajax error: " + statusText;
        setErrorMessage(errorText);
        dispatch(addExLog({ content: errorText, isError: true }));
        dispatch(updateStyles({
          loadingNode: { display: "none" },
          errorMsgStyles: { display: "block" }
        }));
      },
      readBarcodeCallBack: (response: Response) => {
        dispatch(deleteExLog());
        if (oldResponse.current && (oldResponse.current.results.length <= response.results!.length)) {
          dispatch(updateResponse({ response }));
        } else if (!oldResponse.current) {
          dispatch(updateResponse({ response }));
          oldResponse.current = response;
        }
      },
      readBarcodeErrorCallBack: (response: Response, isDecodeError?: boolean) => {
        let errorStr = (isDecodeError ? "decode fail: " : "ajax error: ") + response.message;
        if (response.message === "abort") {
          dispatch(addExLog({ content: errorStr + "\nThe setting changed during the decoding process, abort previous settings." + response.message, isError: false }));
        } else {
          if (response.isExpired) {
            apiCallbacks.uploadImg(true);
          } else {
            setErrorMessage(errorStr);
            dispatch(addExLog({ content: errorStr, isError: true }));
            dispatch(updateStyles({
              loadingNode: { display: "none" },
              errorMsgStyles: { display: "block" }
            }));
          }
        }
      },
      readBarcodeCompleteCallBack: (response: Response) => {
        if (Requester.readBarcodeXhr.length) {
          dispatch(updateState({ stateName: "loadingContent", value: `Second attempt at decoding` }));
          dispatch(updateStyles({
            loadingNode: { display: "block" },
            loadingAnimationNode: { display: "none" }
          }));

        } else {
          if (!response.isExpired) {
            dispatch(updateStyles({ loadingNode: { display: "none" } }));
            dispatch(updateState({ stateName: "loadingContent", value: "" }));
          }
          dispatch(updateStyles({ loadingAnimationNode: { display: "block" } }));
        }
      }
    }
  }, [dispatch, generalStates.curImgIdx, generalStates.decodingMode, globalSettings])

  useEffect(() => {
    imageDataRef.current = imagesData;
    nodeStylesRef.current = nodeStyles;
  }, [imagesData, nodeStyles])

  useEffect(() => {
    if (imagesData.length > 5) {
      if (device() === "mobile") {
        panelRef.current!.scrollTop = panelRef.current!.scrollHeight - panelRef.current!.offsetHeight;
        panelRef.current!.scrollLeft = 0;
      } else {
        panelRef.current!.scrollTop = 0;
        panelRef.current!.scrollLeft = panelRef.current!.scrollWidth - panelRef.current!.offsetWidth;
      }
      dispatch(updateState({ stateName: "curImgIdx", value: imagesData.length - 1 }));
      dispatch(updateStyles({ fileUploadArea: { borderColor: nodeStylesRef.current.fileUploadArea.borderColor, display: "none" } }));
    }
  }, [dispatch, imagesData.length])

  useEffect(() => {
    timer.current && clearTimeout(timer.current);
    if (generalStates.curImgIdx < 0) return;
    dispatch(updateStyles({ loadingNode: { display: "none" } }));
    const currentImage = imageDataRef.current[generalStates.curImgIdx];
    Requester.cancelUploadImgRequest();
    Requester.cancelRequest();
    timer.current = setTimeout(async () => {
      dispatch(updateStyles({ loadingNode: { display: "block" } }));
      if (!currentImage.uploadState) {
        apiCallbacks.uploadImg();
      } else {
        const template = warpTemplateFromGlobalSettings(globalSettings);
        apiCallbacks.decode(template);
      }
    }, 1000);
  }, [generalStates.curImgIdx, globalSettings, apiCallbacks, dispatch])

  const styles = {
    "--width": `calc(82% - ${generalStates.settingsToggle ? "22px" : "330px"})`,
    "--display": nodeStyles.imageListWrapperStyles.display
  } as CSSProperties;

  return (
    <div className="dbr-image-viewer" style={styles} ref={contentRef}>
      <ViewerHeader />
      <ViewerMain
        errorMessage={errorMessage}
        contentRef={contentRef}
        logPreRef={logPreRef}
      />
      <div className="dbr-overflow-hidden">
        <ViewerFooter panelRef={panelRef} />
      </div>
    </div>
  );
}